import React, { useState, useEffect, useCallback } from "react";
import {
  AlertTriangle, Truck, RotateCcw, XCircle, Plus, Phone, Mail, Globe, Users,
  Send, FileText,
} from "lucide-react";
import { Modal } from "./Modal";
import { Badge, StatusBadge, DisciplineBadge } from "./Badges";
import api, { apiErr } from "../lib/api";
import { fmtDateTime, fmtDate } from "../lib/format";
import { toast } from "sonner";

async function openPdfBlob(path) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const METHODS = [
  { value: "email", label: "Email", icon: Mail },
  { value: "phone", label: "Phone", icon: Phone },
  { value: "portal", label: "Portal", icon: Globe },
  { value: "other", label: "Other", icon: Users },
];

function EmailPreviewModal({ itemId, draft, onClose, onLogged }) {
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");
  const [busy, setBusy] = useState(false);

  const logAsSent = async (alsoOpenMail) => {
    setBusy(true);
    try {
      await api.post(`/job-items/${itemId}/customer-advised`, {
        customer_contact: `${draft.to_name ? draft.to_name + " · " : ""}${draft.to_email}`,
        method: "email",
        notes: `Sent unserviceable notice via email${draft.photo_count ? ` (${draft.photo_count} photo(s) on file)` : ""}.\n\nSubject: ${subject}`,
        customer_response: "",
        email_sent: true,
      });
      toast.success("Customer-advised log updated");
      if (alsoOpenMail) {
        const mailto = `mailto:${encodeURIComponent(draft.to_email)}` +
          `?subject=${encodeURIComponent(subject)}` +
          `&body=${encodeURIComponent(body)}`;
        window.location.href = mailto;
      }
      onLogged && onLogged();
      onClose && onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openInMail = () => {
    const mailto = `mailto:${encodeURIComponent(draft.to_email)}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;
    if (mailto.length > 8000) toast.warning("Email body is long — your mail client may truncate it");
    window.location.href = mailto;
  };

  return (
    <Modal open onClose={onClose} icon={Send} wide
      title="Draft Customer Email — Unserviceable Notice"
      subtitle={`To: ${draft.to_name ? draft.to_name + " · " : ""}${draft.to_email}`}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy}
            data-testid="email-preview-cancel">Cancel</button>
          <button className="btn ghost" onClick={openInMail} disabled={busy}
            data-testid="email-preview-open">
            Open in mail client
          </button>
          <button className="btn ghost" onClick={() => logAsSent(true)} disabled={busy}
            data-testid="email-preview-send-and-log">
            Open + Mark as sent
          </button>
          <button className="btn gold" onClick={() => logAsSent(false)} disabled={busy}
            data-testid="email-preview-mark-sent">
            <Send size={14} /> {busy ? "Saving…" : "Mark as sent"}
          </button>
        </>
      }>
      <div className="form-grid">
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Subject</label>
          <input className="input" value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="email-preview-subject" />
        </div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Body (editable — final copy is what you send)</label>
          <textarea className="input mono" rows={16} value={body}
            onChange={(e) => setBody(e.target.value)}
            style={{ fontSize: 12.5, lineHeight: 1.55 }}
            data-testid="email-preview-body" />
        </div>
        {draft.photo_count > 0 && (
          <div className="fld" style={{ gridColumn: "span 2" }}>
            <div className="notice" style={{ background: "#fff8e6", border: "1px solid #e3d28a" }}>
              <AlertTriangle size={14} />
              <span style={{ fontSize: 12 }}>
                {draft.photo_count} photo(s) on file — mailto cannot attach files automatically.
                Open in your mail client, then drag-attach the photos from the job-line attachments
                before sending.
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function CustomerAdvisedLog({ itemId, log, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_contact: "", method: "email", notes: "", customer_response: "",
  });
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [preview, setPreview] = useState(null);

  const composeEmail = async () => {
    setDrafting(true);
    try {
      const r = await api.get(`/job-items/${itemId}/unserviceable/email-draft`);
      const d = r.data || {};
      if (!d.to_email) {
        toast.error("No customer email on file — add one to the job header first");
        setDrafting(false);
        return;
      }
      setPreview(d);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setDrafting(false);
    }
  };

  const submit = async () => {
    if (!form.customer_contact.trim()) return toast.error("Customer contact is required");
    setBusy(true);
    try {
      await api.post(`/job-items/${itemId}/customer-advised`, form);
      toast.success("Customer-advised entry logged");
      setForm({ customer_contact: "", method: "email", notes: "", customer_response: "" });
      setShowForm(false);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card pad" style={{ background: "var(--card-2)", marginTop: 14 }}
      data-testid="customer-advised-log">
      <div className="sec-h" style={{ marginTop: 0 }}>
        <h3 style={{ fontSize: 13 }}>Customer-advised log</h3>
        <span style={{ flex: 1 }} />
        <button className="btn ghost sm" onClick={composeEmail} disabled={drafting}
          title="Generate a templated unserviceable notice ready for review"
          data-testid="customer-advised-email">
          <Send size={12} /> {drafting ? "Drafting…" : "Draft Customer Email"}
        </button>
        {!showForm && (
          <button className="btn ghost sm" onClick={() => setShowForm(true)}
            data-testid="customer-advised-add">
            <Plus size={12} /> Add entry
          </button>
        )}
      </div>
      {showForm && (
        <div className="form-grid" style={{ marginBottom: 10 }}>
          <div className="fld">
            <label>Customer contact</label>
            <input className="input" value={form.customer_contact}
              onChange={(e) => setForm({ ...form, customer_contact: e.target.value })}
              placeholder="Name & email or phone"
              data-testid="customer-advised-contact" />
          </div>
          <div className="fld">
            <label>Method</label>
            <select className="input" value={form.method}
              onChange={(e) => setForm({ ...form, method: e.target.value })}
              data-testid="customer-advised-method">
              {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div className="fld" style={{ gridColumn: "span 2" }}>
            <label>Notes (what was communicated)</label>
            <textarea className="input" rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="customer-advised-notes" />
          </div>
          <div className="fld" style={{ gridColumn: "span 2" }}>
            <label>Customer response / decision</label>
            <textarea className="input" rows={2} value={form.customer_response}
              onChange={(e) => setForm({ ...form, customer_response: e.target.value })}
              placeholder="e.g. Customer asked us to scrap; will collect docs"
              data-testid="customer-advised-response" />
          </div>
          <div className="fld" style={{ gridColumn: "span 2", display: "flex", gap: 8 }}>
            <button className="btn ghost sm" onClick={() => setShowForm(false)} disabled={busy}>
              Cancel
            </button>
            <button className="btn gold sm" onClick={submit} disabled={busy}
              data-testid="customer-advised-save">
              {busy ? "Saving…" : "Save entry"}
            </button>
          </div>
        </div>
      )}
      {(log || []).length === 0 && !showForm && (
        <div className="t-mut" style={{ fontSize: 12 }}>No customer-advised entries yet.</div>
      )}
      {(log || []).length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex",
          flexDirection: "column", gap: 8 }}>
          {(log || []).map((e) => (
            <li key={e.id} className="card pad" style={{ background: "var(--card)" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center",
                flexWrap: "wrap", fontSize: 12 }}>
                <span className="t-strong">{e.customer_contact}</span>
                <Badge label={e.method} cls="b-slate" />
                <span className="t-mut">{fmtDateTime(e.at)}</span>
                <span className="t-mut">· by {e.by_name || "—"}</span>
              </div>
              {e.notes && <div style={{ fontSize: 12.5, marginTop: 6 }}>{e.notes}</div>}
              {e.customer_response && (
                <div style={{ fontSize: 12, marginTop: 6, paddingTop: 6,
                  borderTop: "1px dashed var(--line)" }}>
                  <span className="t-mut">Response:</span> {e.customer_response}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {preview && (
        <EmailPreviewModal itemId={itemId} draft={preview}
          onClose={() => setPreview(null)}
          onLogged={() => onChanged && onChanged()} />
      )}
    </div>
  );
}

export function UnserviceableReviewModal({ itemId, onClose, onChanged, readOnly = false }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [instructions, setInstructions] = useState("");
  const [techId, setTechId] = useState("");
  const [closureNote, setClosureNote] = useState("");
  const [markOutOfService, setMarkOutOfService] = useState(false);
  const [busy, setBusy] = useState(false);
  const [techs, setTechs] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  const downloadReport = async () => {
    try {
      await openPdfBlob(`/job-items/${itemId}/nonconforming-report.pdf`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sendNotice = async () => {
    setConfirmSend(false);
    setSending(true);
    try {
      const r = await api.post(`/job-items/${itemId}/unserviceable/send-email`);
      if (r.data.ok) {
        toast.success(`Notice + report sent to ${r.data.to}` +
          (r.data.intended && r.data.intended !== r.data.to ? ` (intended ${r.data.intended})` : ""));
      } else {
        toast.error(`Not sent: ${r.data.reason || r.data.error || "unknown"}`);
      }
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/job-items/${itemId}`);
      setItem(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  // Lazily load the technician list when admin selects "return to technician"
  const loadTechs = useCallback(async () => {
    try {
      const r = await api.get("/users/staff", { params: { limit: 200 } });
      const list = (r.data?.items || []).filter((u) => u.role === "technician" && u.active !== false);
      setTechs(list);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (action === "return_to_technician" && techs.length === 0) loadTechs();
  }, [action, techs.length, loadTechs]);

  const submit = async () => {
    if (!action) return toast.error("Pick a follow-up action");
    if (action === "return_to_technician" && (!instructions.trim() || !techId)) {
      return toast.error("Pick a technician AND provide instructions");
    }
    if (action === "close" && !closureNote.trim()) {
      return toast.error("Closure note is required");
    }
    setBusy(true);
    try {
      await api.post(`/job-items/${itemId}/unserviceable-review`, {
        action,
        instructions: instructions.trim() || null,
        technician_id: techId || null,
        closure_note: closureNote.trim() || null,
        mark_equipment_out_of_service: action === "close" ? markOutOfService : false,
      });
      toast.success("Review saved");
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (loading || !item) {
    return <Modal open title="Loading…" onClose={onClose}><div className="t-mut">Loading…</div></Modal>;
  }
  const u = item.unserviceable || {};
  const reviewed = !!u.review_outcome;

  return (
    <Modal open onClose={onClose} icon={AlertTriangle} wide
      title={`Unserviceable review · ${item.description || "Item"}`}
      subtitle={`DN ${item.delivery_note_number || "—"} · ${item.customer_name || "—"}`}
      footer={
        !readOnly && !reviewed && (
          <>
            <button className="btn ghost" onClick={onClose} disabled={busy}
              data-testid="usr-review-cancel">Cancel</button>
            <button className="btn gold" onClick={submit}
              disabled={busy || !action ||
                (action === "return_to_technician" && (!instructions.trim() || !techId)) ||
                (action === "close" && !closureNote.trim())}
              data-testid="usr-review-submit">
              {busy ? "Saving…" : "Save review"}
            </button>
          </>
        )
      }>
      {/* Header block */}
      <div className="card pad" style={{ background: "var(--card-2)", marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <StatusBadge status={item.status} />
          <DisciplineBadge name={item.discipline} />
          {item.serial_number && <span className="mono t-mut" style={{ fontSize: 12 }}>SN {item.serial_number}</span>}
          {item.issue_number && <span className="mono t-mut" style={{ fontSize: 12 }}>IN {item.issue_number}</span>}
        </div>
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, fontSize: 12.5 }}>
          <div><span className="t-mut">Reason:</span> <b>{u.reason_label || u.reason || "—"}</b></div>
          <div><span className="t-mut">Raised by:</span> <b>{u.raised_by_name || "—"}</b> · {fmtDateTime(u.raised_at)}</div>
          {(u.attachment_ids || []).length > 0 && (
            <div style={{ gridColumn: "span 2" }}>
              <span className="t-mut">Attachments:</span> <b>{(u.attachment_ids || []).length}</b> file(s) on this job-line
            </div>
          )}
          <div style={{ gridColumn: "span 2" }}>
            <div className="t-mut" style={{ marginBottom: 4 }}>Technician notes</div>
            <div style={{ whiteSpace: "pre-wrap", padding: 8, background: "var(--card)",
              border: "1px solid var(--line)", borderRadius: 6 }}>
              {u.technician_notes || "—"}
            </div>
          </div>
          <div style={{ gridColumn: "span 2", display: "flex", gap: 8, flexWrap: "wrap",
            alignItems: "center", marginTop: 4 }}>
            {u.report_number && <Badge label={`Report ${u.report_number}`} cls="b-slate" />}
            <button className="btn ghost sm" onClick={downloadReport}
              data-testid="usr-download-report">
              <FileText size={12} /> Non-Conforming Report (PDF)
            </button>
            {!readOnly && (
              <button className="btn gold sm" onClick={() => setConfirmSend(true)} disabled={sending}
                data-testid="usr-send-notice">
                <Send size={12} /> {sending ? "Sending…" : "Email notice to customer"}
              </button>
            )}
          </div>
        </div>
      </div>

      {confirmSend && (
        <Modal open onClose={() => setConfirmSend(false)} icon={Send}
          title="Email non-conforming notice"
          subtitle={`${item.customer_name || "Customer"} · DN ${item.delivery_note_number || "—"}`}
          footer={<>
            <button className="btn ghost" onClick={() => setConfirmSend(false)} disabled={sending}
              data-testid="usr-send-cancel">Cancel</button>
            <button className="btn gold" onClick={sendNotice} disabled={sending}
              data-testid="usr-send-confirm">
              <Send size={13} /> {sending ? "Sending…" : "Send notice + report"}
            </button>
          </>}>
          <p style={{ fontSize: 13, lineHeight: 1.5 }}>
            This emails the customer's contact on file the non-conforming notice with the
            <b> Equipment Non-Conforming Report</b> PDF attached (plus any technician photos).
            The send is logged to the customer-advised timeline. Honours <b>TEST MODE</b> and the
            Settings → Emails toggle.
          </p>
        </Modal>
      )}

      {reviewed && (
        <div className="notice" style={{ background: "#eafbea", border: "1px solid #c7e8c7", marginBottom: 12 }}
          data-testid="usr-review-already">
          <span style={{ fontSize: 13 }}>
            <b>Already reviewed</b> · {u.review_outcome?.replace(/_/g, " ")} by {u.review_by_name || "—"}
            {u.review_at && <> · {fmtDateTime(u.review_at)}</>}
            {u.review_note && <div style={{ marginTop: 4, fontStyle: "italic" }}>“{u.review_note}”</div>}
          </span>
        </div>
      )}

      {!readOnly && !reviewed && (
        <>
          <div className="t-strong" style={{ fontSize: 13, marginBottom: 8 }}>Choose follow-up action</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
            <ActionCard k="return_to_customer" current={action} setAction={setAction}
              icon={Truck} label="Return to customer"
              hint="Move to Dispatch / In Dispatch." testid="usr-act-customer" />
            <ActionCard k="return_to_technician" current={action} setAction={setAction}
              icon={RotateCcw} label="Return to technician"
              hint="Send back with new instructions." testid="usr-act-tech" />
            <ActionCard k="close" current={action} setAction={setAction}
              icon={XCircle} label="Close item"
              hint="Close & remove from active queues." testid="usr-act-close" tone="danger" />
          </div>

          {action === "return_to_technician" && (
            <div className="form-grid" data-testid="usr-form-tech">
              <div className="fld">
                <label>Technician <span className="req">*</span></label>
                <select className="input" value={techId} onChange={(e) => setTechId(e.target.value)}
                  data-testid="usr-tech-id">
                  <option value="">— Select technician —</option>
                  {techs.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.discipline ? ` · ${t.discipline}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fld" style={{ gridColumn: "span 2" }}>
                <label>Instructions for technician <span className="req">*</span></label>
                <textarea className="input" rows={3} value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="What should the technician do next?"
                  data-testid="usr-instructions" />
              </div>
            </div>
          )}

          {action === "close" && (
            <div className="form-grid" data-testid="usr-form-close">
              <div className="fld" style={{ gridColumn: "span 2" }}>
                <label>Closure note <span className="req">*</span></label>
                <textarea className="input" rows={3} value={closureNote}
                  onChange={(e) => setClosureNote(e.target.value)}
                  placeholder="Why is this being closed? (kept on file)"
                  data-testid="usr-closure-note" />
              </div>
              <div className="fld" style={{ gridColumn: "span 2" }}>
                <label style={{ display: "flex", gap: 8, alignItems: "flex-start",
                  cursor: "pointer", padding: 10, border: "1px solid var(--line)",
                  borderRadius: 8, background: markOutOfService ? "#fdecea" : "var(--card)" }}>
                  <input type="checkbox" checked={markOutOfService}
                    onChange={(e) => setMarkOutOfService(e.target.checked)}
                    style={{ marginTop: 2 }}
                    data-testid="usr-mark-out-of-service" />
                  <span style={{ fontSize: 12.5 }}>
                    <b>Mark the equipment as permanently Out of Service.</b><br />
                    <span className="t-mut">
                      The equipment record will be flagged Out of Service, excluded
                      from future calibration-due reports, and kept visible in
                      customer history. Do NOT tick this if the customer may collect
                      or repair the item later.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
        </>
      )}

      {/* Customer-advised log (always visible to admin) */}
      <CustomerAdvisedLog itemId={itemId} log={item.customer_advised_log || []}
        onChanged={() => { load(); onChanged && onChanged(); }} />
    </Modal>
  );
}

function ActionCard({ k, current, setAction, icon: Icon, label, hint, testid, tone }) {
  const active = current === k;
  const danger = tone === "danger";
  return (
    <button type="button" className="card pad"
      data-testid={testid}
      onClick={() => setAction(k)}
      style={{
        textAlign: "left", cursor: "pointer",
        background: active ? (danger ? "#fdecea" : "#fff8e6") : "var(--card)",
        borderColor: active ? (danger ? "#f3c7c2" : "#e3d28a") : undefined,
        padding: 12, display: "flex", gap: 10, alignItems: "flex-start",
      }}>
      <Icon size={18} style={{ color: danger ? "var(--red)" : "var(--gold-deep)", marginTop: 2, flex: "none" }} />
      <div style={{ minWidth: 0 }}>
        <div className="t-strong" style={{ fontSize: 13 }}>{label}</div>
        <div className="t-mut" style={{ fontSize: 11.5, marginTop: 2 }}>{hint}</div>
      </div>
    </button>
  );
}
